import apiClient from '../client'
import type { PageResponse } from '../types'
import type {
  MunicipalityResponse,
  MunicipalityOptionResponse,
  MunicipalityCreateRequest,
  MunicipalityUpdateRequest,
  MunicipalityOcrMappingUpdateRequest,
  OcrCaseApprovalRequest,
  OcrCaseApprovalResponse,
  OcrCaseFieldValueResponse,
  OcrCaseFieldValueUpdateRequest,
  OcrMatchCandidate,
  OcrMatchCandidatesResponse,
  OcrSystemKeyCatalogResponse,
  PublicMunicipalityResponse,
} from './types'

export const municipalitiesApi = {
  list() {
    return apiClient.get<MunicipalityResponse[]>('/api/municipalities')
  },

  /**
   * 首页の自治体セレクター（SYSTEM_ADMIN 専用）用の軽量一覧。
   * 有効な自治体の id/code/name のみ（銀行等の機微項目を含まない）を code 昇順で返す。
   * 失敗時はセレクターを「全自治体」のみに縮退させるだけなのでグローバルエラー表示は抑止する（_silent）。
   */
  options() {
    return apiClient.get<MunicipalityOptionResponse[]>('/api/municipalities/options', {
      _silent: true,
    })
  },

  search(params?: {
    code?: string
    name?: string
    prefecture?: string
    status?: string
    ocrUse?: boolean
    sortBy?: string
    order?: 'ASC' | 'DESC'
    page?: number
    size?: number
  }) {
    return apiClient.get<PageResponse<MunicipalityResponse>>('/api/municipalities/search', { params })
  },

  getById(id: number) {
    return apiClient.get<MunicipalityResponse>(`/api/municipalities/${id}`)
  },

  create(data: MunicipalityCreateRequest) {
    return apiClient.post<MunicipalityResponse>('/api/municipalities', data, { _suppressToastOnFieldErrors: true })
  },

  update(id: number, data: MunicipalityUpdateRequest) {
    return apiClient.put<MunicipalityResponse>(`/api/municipalities/${id}`, data, { _suppressToastOnFieldErrors: true })
  },

  updateOcrMapping(id: number, data: MunicipalityOcrMappingUpdateRequest) {
    return apiClient.put<MunicipalityResponse>(`/api/municipalities/${id}/ocr-mapping`, data)
  },

  /** 申出書 OCR マッピング（moushidesho_ocr_mapping_json）のみ更新 */
  updateMoushideshoOcrMapping(id: number, data: MunicipalityOcrMappingUpdateRequest) {
    return apiClient.put<MunicipalityResponse>(`/api/municipalities/${id}/moushidesho-ocr-mapping`, data)
  },

  /** docType 省略時は保護台帳（受給者系）。MOUSHIDESHO は申出書系＋受給者系のカタログ。 */
  systemKeyCatalog(docType?: 'PROTECTION_LEDGER' | 'MOUSHIDESHO') {
    return apiClient.get<OcrSystemKeyCatalogResponse[]>('/api/municipalities/system-key-catalog', {
      params: docType ? { docType } : undefined,
    })
  },

  /**
   * ログイン前ヘッダ表示用。subdomain → 自治体名解決の公開エンドポイント。
   * 該当なしは 404 を返すので呼び出し側で握り潰す想定（{ _silent: true }）。
   */
  publicGetBySubdomain(subdomain: string) {
    return apiClient.get<PublicMunicipalityResponse>(
      `/api/public/municipalities/by-subdomain/${encodeURIComponent(subdomain)}`,
      { _silent: true },
    )
  },
}

export const ocrCaseFieldValuesApi = {
  list(caseId: number) {
    return apiClient.get<OcrCaseFieldValueResponse[]>(`/api/ocr-cases/${caseId}/field-values`)
  },

  update(caseId: number, updates: OcrCaseFieldValueUpdateRequest[]) {
    return apiClient.put<OcrCaseFieldValueResponse[]>(`/api/ocr-cases/${caseId}/field-values`, updates)
  },

  approve(caseId: number, request?: OcrCaseApprovalRequest) {
    // 申出書 OCR は照合の決定を body で渡す。保護台帳 OCR / 未指定は body 省略で従来どおり新規作成。
    return apiClient.post<OcrCaseApprovalResponse>(`/api/ocr-cases/${caseId}/approve`, request ?? {})
  },

  /** 申出書 OCR の照合候補（③）を取得。保護台帳 OCR / 既登録は candidates 空で返る。 */
  matchCandidates(caseId: number) {
    return apiClient.get<OcrMatchCandidatesResponse>(`/api/ocr-cases/${caseId}/match-candidates`)
  },

  /** 既存受給者の手動検索（③補助）。氏名/宛名/住所/識別ID の部分一致で候補を分頁取得。 */
  searchRecipients(caseId: number, q: string, page = 0, size = 20) {
    return apiClient.get<PageResponse<OcrMatchCandidate>>(`/api/ocr-cases/${caseId}/recipient-search`, {
      params: { q, page, size },
    })
  },

  downloadPdf(caseId: number) {
    return apiClient.get(`/api/ocr-cases/${caseId}/pdf`, { responseType: 'blob' })
  },
}
